import React from 'react';
import PropTypes from 'prop-types';
import {
  LuBookCheck,
  LuBuilding2,
  LuCamera,
  LuCheck,
  LuEye,
  LuFolderKanban,
  LuHouse,
  LuImage,
  LuKeyRound,
  LuMail,
  LuMessagesSquare,
  LuPencil,
  LuPhone,
  LuPhoneCall,
  LuPlus,
  LuStar,
} from 'react-icons/lu';
import { useTranslation } from '../../../../../i18n/LanguageContext';
import { STAGE_H, STAGE_W, abs } from '../../engine/stage';
import { COMPLETION_POP, CONTACT_EMERGENCY, CONTACT_HOME, CONTACT_WORK, HERO } from '../../layout';
import { Abs, Btn, CARD, Dialog, DialogIcon, Field, GLASS_CHIP, GLASS_POPOVER, Note, TILE } from '../kit';

/** Drawn portrait used once a photo is "uploaded". */
export const Portrait = ({ size, rounded = 'tw-rounded-2xl' }) => (
  <span style={{ width: size, height: size }} className={`tw-relative tw-block tw-overflow-hidden tw-bg-gradient-to-br tw-from-sky-200 tw-to-indigo-300 ${rounded}`}>
    <svg viewBox="0 0 80 80" className="tw-absolute tw-inset-0 tw-h-full tw-w-full" aria-hidden="true">
      <circle cx="40" cy="31" r="15" fill="#f1c7a5" />
      <path d="M22 28c0-12 8-19 18-19s18 7 18 19c-3-6-9-8-18-8s-15 2-18 8z" fill="#5b3a29" />
      <path d="M12 80c2-17 13-26 28-26s26 9 28 26z" fill="#0d9488" />
    </svg>
  </span>
);

Portrait.propTypes = { size: PropTypes.number.isRequired, rounded: PropTypes.string };

const Ring = ({ percent, size = 40 }) => {
  const r = size / 2 - 4;
  const c = 2 * Math.PI * r;
  let tone = '#f59e0b';
  if (percent >= 100) tone = '#10b981';
  else if (percent >= 60) tone = '#14b8a6';
  return (
    <span style={{ width: size, height: size }} className="tw-relative tw-grid tw-shrink-0 tw-place-items-center">
      <svg viewBox={`0 0 ${size} ${size}`} className="tw-absolute tw-inset-0 -tw-rotate-90" aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth="4" className="tw-text-slate-200 dark:tw-text-white/10" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={tone} strokeWidth="4" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - percent / 100)} style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      </svg>
      <span className="tw-text-[11px] tw-font-bold tw-tabular-nums tw-text-slate-800 dark:tw-text-slate-100">{percent}</span>
    </span>
  );
};

Ring.propTypes = { percent: PropTypes.number.isRequired, size: PropTypes.number };

/** Percent shown on the completion ring for a hero state. */
export const completionOf = (state) => {
  let value = 62;
  if (state.photo) value += 20 * 0.6;
  if (state.emergency) value += 6;
  if (state.home) value += 6;
  return Math.min(100, Math.round(value));
};

/**
 * The profile card: photo with the camera button, greeting / name / role /
 * date, the completion ring, "Şifremi Değiştir", three quick stats and the
 * contact tiles (emergency phone and addresses editable).
 * state: { photo, emergency, home, work, highlightStat, completionOpen }
 */
export const ProfileHero = ({ top, state }) => {
  const { t, locale } = useTranslation();
  const H = HERO(top);
  const label = (key, vars) => t(`profilePage.hero.${key}`, vars);
  const percent = completionOf(state);
  const stats = [
    { key: 'projects', icon: LuFolderKanban, value: '2' },
    { key: 'sops', icon: LuBookCheck, value: '6/8' },
    { key: 'messages', icon: LuMessagesSquare, value: '2', alert: true },
  ];
  const contacts = [
    { key: 'phone', icon: LuPhone, value: '0532 123 45 67' },
    { key: 'emergency', icon: LuPhoneCall, value: state.emergency, editable: true },
    { key: 'email', icon: LuMail, value: 'ayse.kaya@habcarecro.com' },
    { key: 'home', icon: LuHouse, value: state.home, editable: true, primary: Boolean(state.home) },
    { key: 'work', icon: LuBuilding2, value: state.work, editable: true },
  ];
  return (
    <div style={abs(H.card)} className={`tw-absolute tw-overflow-hidden tw-rounded-[1.75rem] ${CARD} tw-bg-gradient-to-br tw-from-teal-50/80 tw-to-white/70 dark:tw-from-teal-500/[0.06] dark:tw-to-transparent`}>
      <div className="tw-absolute" style={{ left: -H.card.x, top: -H.card.y, width: STAGE_W, height: STAGE_H }}>
        <span style={abs(H.avatar)} className="tw-absolute tw-overflow-hidden tw-rounded-2xl tw-shadow-lg tw-shadow-teal-500/20">
          {state.photo ? <Portrait size={H.avatar.w} /> : (
            <span className="tw-grid tw-h-full tw-w-full tw-place-items-center tw-bg-gradient-to-br tw-from-teal-400 tw-to-cyan-600 tw-text-2xl tw-font-bold tw-text-white">AK</span>
          )}
        </span>
        <span style={abs(H.camera)} className={`tw-absolute tw-grid tw-place-items-center tw-rounded-full tw-bg-slate-900 tw-text-white tw-ring-2 tw-ring-white ${state.cameraPressed ? 'tw-scale-90' : ''}`}>
          <LuCamera aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
        </span>
        <div style={{ left: H.name.x, top: H.name.y }} className="tw-absolute">
          <p className="tw-text-[13px] tw-text-slate-500">{t('common.greeting.afternoon')},</p>
          <p className="tw-text-[26px] tw-font-semibold tw-leading-tight tw-text-slate-900 dark:tw-text-white">Ayşe Kaya</p>
          <p className="tw-mt-1 tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-slate-500">
            <span className="tw-rounded-full tw-bg-teal-500/10 tw-px-2 tw-py-0.5 tw-font-semibold tw-text-teal-700 dark:tw-text-teal-300">Study Coordinator</span>
            {new Date().toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' })}
          </p>
        </div>
        <span style={abs(H.completion)} className={`tw-absolute tw-flex tw-items-center tw-gap-2.5 tw-rounded-2xl tw-px-2.5 ${GLASS_CHIP} dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-bg-none ${state.completionOpen ? 'tw-ring-2 tw-ring-teal-500/40' : ''}`}>
          <Ring percent={percent} />
          <span className="tw-min-w-0">
            <span className="tw-block tw-truncate tw-text-xs tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{label('completion.title')}</span>
            <span className="tw-block tw-truncate tw-text-[11px] tw-text-slate-500">{percent >= 100 ? label('completion.complete') : label('completion.seeMissing')}</span>
          </span>
        </span>
        <Btn rect={H.password} icon={LuKeyRound} pressed={state.passwordPressed}>{label('changePassword')}</Btn>
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <span key={stat.key} style={abs(H.stat(index))} className={`tw-absolute tw-flex tw-items-center tw-gap-3 tw-px-3 ${TILE} ${state.highlightStat === stat.key ? 'tw-ring-2 tw-ring-teal-400/60' : ''}`}>
              <span className={`tw-grid tw-h-9 tw-w-9 tw-place-items-center tw-rounded-xl ${stat.alert ? 'tw-bg-rose-500/10 tw-text-rose-600' : 'tw-bg-teal-500/10 tw-text-teal-600 dark:tw-text-teal-300'}`}>
                <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
              </span>
              <span>
                <span className={`tw-block tw-text-lg tw-font-semibold tw-leading-tight tw-tabular-nums ${stat.alert ? 'tw-text-rose-600' : 'tw-text-slate-900 dark:tw-text-white'}`}>{stat.value}</span>
                <span className="tw-block tw-text-[11px] tw-text-slate-500">{label(`stats.${stat.key}`)}</span>
              </span>
            </span>
          );
        })}
        <span style={{ left: H.contacts.x, top: H.contacts.y }} className="tw-absolute tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400">{label('contacts.title')}</span>
        {contacts.map((contact, index) => {
          const Icon = contact.icon;
          const rect = H.contact(index);
          const empty = !contact.value;
          return (
            <span key={contact.key} style={abs(rect)} className={`tw-absolute tw-flex tw-items-center tw-gap-3 tw-px-3 ${TILE} ${state.flash === contact.key ? 'tw-ring-2 tw-ring-emerald-400/70' : ''}`}>
              <span className="tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl tw-bg-slate-900/[0.05] tw-text-slate-500 dark:tw-bg-white/[0.07]">
                <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
              </span>
              <span className="tw-min-w-0 tw-flex-1">
                <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-text-slate-500">
                  {label(`contacts.${contact.key}`)}
                  {contact.primary && <span className="tw-inline-flex tw-items-center tw-gap-0.5 tw-rounded-full tw-bg-amber-400/20 tw-px-1.5 tw-text-[10px] tw-font-semibold tw-text-amber-700"><LuStar aria-hidden="true" className="tw-h-2.5 tw-w-2.5" />{label('contacts.primary')}</span>}
                </span>
                <span className={`tw-block tw-truncate tw-text-sm ${empty ? 'tw-italic tw-text-slate-400' : 'tw-font-medium tw-text-slate-800 dark:tw-text-slate-100'} ${contact.editable ? 'tw-pr-16' : ''}`}>
                  {contact.value || (contact.editable ? label('contacts.notAdded') : label('contacts.none'))}
                </span>
              </span>
              {contact.editable && (
                <span style={{ left: rect.w - 74, top: 14 }} className="tw-absolute tw-inline-flex tw-h-7 tw-items-center tw-gap-1 tw-rounded-full tw-px-2 tw-text-xs tw-font-semibold tw-text-teal-700 dark:tw-text-teal-300">
                  {empty ? <LuPlus aria-hidden="true" className="tw-h-3.5 tw-w-3.5" /> : <LuPencil aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />}
                  {empty ? label('contacts.add') : label('contacts.edit')}
                </span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
};

ProfileHero.propTypes = { top: PropTypes.number.isRequired, state: PropTypes.object.isRequired };

/** The completion popover under the ring. */
export const CompletionPopover = ({ top, state }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.hero.completion.${key}`, vars);
  const P = COMPLETION_POP(top);
  const percent = completionOf(state);
  const rows = [
    { key: 'personal', detail: label('personalDetail', { filled: state.emergency && state.home ? 7 : 5, total: 7 }), share: state.emergency && state.home ? 100 : 71 },
    { key: 'photo', detail: state.photo ? label('done') : label('missing'), share: state.photo ? 100 : 0 },
    { key: 'education', detail: label('educationDetail', { completed: 6, total: 8 }), share: 75 },
  ];
  const suggestions = [
    !state.photo && 'photo',
    !state.emergency && 'emergencyPhone',
    !state.home && 'address',
    'education',
  ].filter(Boolean);
  return (
    <div style={abs(P.panel)} className={`tw-absolute tw-z-[9] tw-rounded-2xl tw-p-4 ${GLASS_POPOVER}`}>
      <div className="tw-flex tw-items-center tw-gap-3">
        <Ring percent={percent} size={52} />
        <div className="tw-min-w-0">
          <p className="tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{label('title')}</p>
          <p className="tw-text-[11px] tw-leading-snug tw-text-slate-500">{label('explain')}</p>
        </div>
      </div>
      <div className="tw-mt-4 tw-space-y-3">
        {rows.map((row) => (
          <div key={row.key}>
            <p className="tw-flex tw-justify-between tw-text-xs"><span className="tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{label(`rows.${row.key}`)}</span><span className="tw-text-slate-500">{row.detail}</span></p>
            <span className="tw-mt-1 tw-block tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10">
              <span style={{ width: `${row.share}%` }} className={`tw-block tw-h-full tw-rounded-full tw-transition-[width] tw-duration-500 ${row.share >= 100 ? 'tw-bg-emerald-500' : 'tw-bg-teal-500'}`} />
            </span>
          </div>
        ))}
      </div>
      <p className="tw-mt-4 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400">{label('todo')}</p>
      <div className="tw-mt-2 tw-space-y-1.5">
        {suggestions.map((key) => (
          <span key={key} className={`tw-flex tw-h-8 tw-items-center tw-justify-between tw-rounded-xl tw-px-3 tw-text-xs tw-font-semibold tw-text-teal-800 dark:tw-text-teal-200 ${state.suggestionFocus === key ? 'tw-bg-teal-500/20' : 'tw-bg-teal-500/[0.08]'}`}>
            {label(`suggestions.${key}`)}
            <span aria-hidden="true">→</span>
          </span>
        ))}
      </div>
    </div>
  );
};

CompletionPopover.propTypes = { top: PropTypes.number.isRequired, state: PropTypes.object.isRequired };

/** Position of a completion suggestion button (for the cursor). */
export const suggestionRect = (top, state, key) => {
  const keys = [!state.photo && 'photo', !state.emergency && 'emergencyPhone', !state.home && 'address', 'education'].filter(Boolean);
  const index = Math.max(0, keys.indexOf(key));
  const P = COMPLETION_POP(top);
  return { x: P.panel.x + 16, y: P.panel.y + 220 + index * 38, w: P.panel.w - 32, h: 32 };
};

// ── Hero dialogs ────────────────────────────────────────────────────────
export const PHOTO_DLG = {
  dialog: { x: 340, y: 90, w: 600, h: 540 },
  previews: { x: 364, y: 182, w: 552, h: 178 },
  another: { x: 576, y: 580, w: 166, h: 38 },
  save: { x: 752, y: 580, w: 168, h: 38 },
};

export const PhotoDialog = ({ appear, saving }) => {
  const { t } = useTranslation();
  const label = (key) => t(`profilePage.photo.${key}`);
  return (
    <Dialog rect={PHOTO_DLG.dialog} appear={appear} footer title={label('title')} subtitle={label('subtitle')} icon={<DialogIcon icon={LuImage} gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" />}>
      <Abs rect={PHOTO_DLG.previews} className={`tw-flex tw-items-end tw-justify-around tw-px-6 tw-pb-4 ${TILE}`}>
        {[['onProfile', 96, 'tw-rounded-2xl'], ['onCard', 64, 'tw-rounded-full'], ['inMenu', 36, 'tw-rounded-full']].map(([key, size, rounded]) => (
          <span key={key} className="tw-flex tw-flex-col tw-items-center tw-gap-2">
            <Portrait size={size} rounded={rounded} />
            <span className="tw-text-xs tw-font-semibold tw-text-slate-500">{label(key)}</span>
          </span>
        ))}
      </Abs>
      <span style={{ left: 366, top: 372 }} className="tw-absolute tw-text-xs tw-text-slate-500">ayse-profil.jpg · 2,4 MB</span>
      <Note rect={{ x: 364, y: 400, w: 552, h: 64 }} icon={LuEye}>{label('tips')}</Note>
      <Btn rect={PHOTO_DLG.another}>{label('another')}</Btn>
      <Btn rect={PHOTO_DLG.save} variant="primary" icon={LuCheck} busy={saving}>{saving ? label('saving') : label('save')}</Btn>
    </Dialog>
  );
};

PhotoDialog.propTypes = { appear: PropTypes.number, saving: PropTypes.bool };

export const PHONE_DLG = {
  dialog: { x: 352, y: 170, w: 576, h: 360 },
  field: { x: 376, y: 290, w: 528, h: 42 },
  save: { x: 808, y: 476, w: 96, h: 38 },
};

export const PhoneDialog = ({ appear, value, focused }) => {
  const { t } = useTranslation();
  const label = (key) => t(`profilePage.dialogs.phone.${key}`);
  return (
    <Dialog rect={PHONE_DLG.dialog} appear={appear} footer title={label('title')} subtitle={label('subtitle')} icon={<DialogIcon icon={LuPhoneCall} gradient="tw-from-rose-400 tw-to-orange-500 tw-shadow-rose-500/30" />}>
      <Field rect={PHONE_DLG.field} label={label('number')} value={value} placeholder={label('placeholder')} focused={focused} hint={label('hint')} />
      <Btn rect={{ ...PHONE_DLG.save, x: PHONE_DLG.save.x - 104 }} variant="ghost">{t('profilePage.common.cancel')}</Btn>
      <Btn rect={PHONE_DLG.save} variant="primary">{t('profilePage.common.save')}</Btn>
    </Dialog>
  );
};

PhoneDialog.propTypes = { appear: PropTypes.number, value: PropTypes.string, focused: PropTypes.bool };

export const ADDRESS_DLG = {
  dialog: { x: 352, y: 96, w: 576, h: 528 },
  home: { x: 376, y: 210, w: 528, h: 66 },
  work: { x: 376, y: 318, w: 528, h: 66 },
  primaryHome: { x: 376, y: 434, w: 256, h: 40 },
  primaryWork: { x: 648, y: 434, w: 256, h: 40 },
  save: { x: 808, y: 570, w: 96, h: 38 },
};

export const AddressDialog = ({ appear, home, work, focus, primary = 'home' }) => {
  const { t } = useTranslation();
  const label = (key) => t(`profilePage.dialogs.address.${key}`);
  const star = (key, rect) => (
    <span style={abs(rect)} className={`tw-absolute tw-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-text-sm tw-font-semibold tw-transition-colors tw-duration-300 ${primary === key ? 'tw-border-amber-400/60 tw-bg-amber-400/15 tw-text-amber-800 dark:tw-text-amber-200' : 'tw-border-slate-200 tw-bg-white/70 tw-text-slate-500 dark:tw-border-white/10 dark:tw-bg-white/[0.03]'} ${key === 'work' && !work ? 'tw-opacity-50' : ''}`}>
      <LuStar aria-hidden="true" className="tw-h-4 tw-w-4" />
      {label(key)}
    </span>
  );
  return (
    <Dialog rect={ADDRESS_DLG.dialog} appear={appear} footer title={label('title')} subtitle={label('subtitle')} icon={<DialogIcon icon={LuHouse} gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" />}>
      <Field rect={ADDRESS_DLG.home} label={label('home')} required area value={home} placeholder={label('homePlaceholder')} focused={focus === 'home'} />
      <Field rect={ADDRESS_DLG.work} label={label('work')} area value={work} placeholder={label('workPlaceholder')} focused={focus === 'work'} hint={label('workHint')} />
      <span style={{ left: 378, top: 410 }} className="tw-absolute tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('primary')}</span>
      {star('home', ADDRESS_DLG.primaryHome)}
      {star('work', ADDRESS_DLG.primaryWork)}
      <span style={{ left: 378, top: 482, width: 524 }} className="tw-absolute tw-text-[11px] tw-leading-snug tw-text-slate-500">{work ? label('primaryHint') : label('primaryHomeOnly')}</span>
      <Btn rect={{ ...ADDRESS_DLG.save, x: ADDRESS_DLG.save.x - 104 }} variant="ghost">{t('profilePage.common.cancel')}</Btn>
      <Btn rect={ADDRESS_DLG.save} variant="primary">{t('profilePage.common.save')}</Btn>
    </Dialog>
  );
};

AddressDialog.propTypes = { appear: PropTypes.number, home: PropTypes.string, work: PropTypes.string, focus: PropTypes.string, primary: PropTypes.string };

export const PASSWORD_DLG = {
  dialog: { x: 352, y: 96, w: 576, h: 528 },
  current: { x: 376, y: 210, w: 528, h: 42 },
  next: { x: 376, y: 286, w: 528, h: 42 },
  confirm: { x: 376, y: 362, w: 528, h: 42 },
  rules: { x: 376, y: 424, w: 528, h: 60 },
  submit: { x: 744, y: 570, w: 160, h: 38 },
};

export const PasswordDialog = ({ appear, filled = 0 }) => {
  const { t } = useTranslation();
  const label = (key) => t(`profilePage.dialogs.password.${key}`);
  const dots = (count) => '•'.repeat(count);
  const rules = [['ruleLength', filled >= 2], ['ruleMatch', filled >= 3]];
  return (
    <Dialog rect={PASSWORD_DLG.dialog} appear={appear} footer title={label('title')} subtitle={label('subtitle')} icon={<DialogIcon icon={LuKeyRound} gradient="tw-from-slate-500 tw-to-slate-700 tw-shadow-slate-500/30" />}>
      <Field rect={PASSWORD_DLG.current} label={label('current')} value={filled >= 1 ? dots(8) : ''} focused={filled === 0.5} />
      <Field rect={PASSWORD_DLG.next} label={label('new')} value={filled >= 2 ? dots(10) : ''} focused={filled === 1.5} />
      <Field rect={PASSWORD_DLG.confirm} label={label('confirm')} value={filled >= 3 ? dots(10) : ''} focused={filled === 2.5} />
      <Abs rect={PASSWORD_DLG.rules} className="tw-space-y-1.5">
        {rules.map(([key, ok]) => (
          <p key={key} className={`tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-transition-colors tw-duration-300 ${ok ? 'tw-text-emerald-600' : 'tw-text-slate-400'}`}>
            <span className={`tw-grid tw-h-4 tw-w-4 tw-place-items-center tw-rounded-full ${ok ? 'tw-bg-emerald-500 tw-text-white' : 'tw-border tw-border-slate-300'}`}>{ok && <LuCheck aria-hidden="true" className="tw-h-3 tw-w-3" />}</span>
            {label(key)}
          </p>
        ))}
      </Abs>
      <Btn rect={{ ...PASSWORD_DLG.submit, x: PASSWORD_DLG.submit.x - 104, w: 96 }} variant="ghost">{t('profilePage.common.cancel')}</Btn>
      <Btn rect={PASSWORD_DLG.submit} variant="primary">{label('submit')}</Btn>
    </Dialog>
  );
};

PasswordDialog.propTypes = { appear: PropTypes.number, filled: PropTypes.number };

export { CONTACT_EMERGENCY, CONTACT_HOME, CONTACT_WORK };
